import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Bot, Check, Sparkles, TrendingUp, Zap, Lock } from "lucide-react";
import { useBotTiers, useUserBots, useActivateBot, useClaimBotProfits, useProfile } from "@/lib/data-hooks";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/bots")({
  head: () => ({ meta: [{ title: "Trading Bots — CryptoBull Pro" }] }),
  component: Bots,
});

const riskColor: Record<string, string> = {
  Low: "text-emerald-400 border-emerald-400/30",
  Medium: "text-amber-400 border-amber-400/30",
  High: "text-rose-400 border-rose-400/30",
};

const tierIcon: Record<string, typeof Bot> = {
  basic: Bot,
  pro: TrendingUp,
  elite: Zap,
};

function Bots() {
  const { data: tiers = [] } = useBotTiers();
  const { data: bots = [] } = useUserBots();
  const { data: profile } = useProfile();
  const activate = useActivateBot();
  const claim = useClaimBotProfits();
  const [openId, setOpenId] = useState<string | null>(null);
  const [amount, setAmount] = useState("");

  const activeBots = bots.filter((b) => b.status === "active");

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Bot className="h-6 w-6 text-primary" /> Automated trading bots</h1>
        <p className="text-sm text-muted-foreground mt-1">Let our algorithms trade for you 24/7. Pick a tier that fits your capital and risk appetite.</p>
      </div>

      {activeBots.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Your active bots</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {activeBots.map((b) => {
              const tier = b.tier;
              if (!tier) return null;
              const start = new Date(b.start_at).getTime();
              const end = new Date(b.end_at).getTime();
              const now = Date.now();
              const elapsedDays = Math.max(0, (Math.min(now, end) - start) / 86400000);
              const accrued = Number(b.amount) * (Number(tier.daily_roi) / 100) * elapsedDays;
              const pending = Math.max(0, accrued - Number(b.claimed));
              const progress = Math.min(100, ((now - start) / (end - start)) * 100);
              return (
                <div key={b.id} className="glass rounded-2xl p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold">{tier.name}</div>
                      <div className="text-xs text-muted-foreground">Started {fmtDate(b.start_at)} · Matures {fmtDate(b.end_at)}</div>
                    </div>
                    <Badge variant="outline" className="text-emerald-400 border-emerald-400/30">Active</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-white/5 p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Invested</div>
                      <div className="font-mono text-sm">{fmtMoney(b.amount)}</div>
                    </div>
                    <div className="rounded-lg bg-white/5 p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Pending</div>
                      <div className="font-mono text-sm text-primary">{fmtMoney(pending)}</div>
                    </div>
                    <div className="rounded-lg bg-white/5 p-2">
                      <div className="text-[10px] uppercase text-muted-foreground">Claimed</div>
                      <div className="font-mono text-sm">{fmtMoney(b.claimed)}</div>
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-primary" style={{ width: `${progress}%` }} />
                  </div>
                  <Button size="sm" className="w-full" disabled={claim.isPending || pending < 0.01} onClick={() => claim.mutate(b.id)}>
                    <Sparkles className="h-4 w-4 mr-1" />
                    {pending < 0.01 ? "No profits yet" : `Claim ${fmtMoney(pending)}`}
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Available bots</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiers.map((t) => {
            const Icon = tierIcon[t.slug] ?? Bot;
            const features = Array.isArray(t.features) ? (t.features as string[]) : [];
            return (
              <div key={t.id} className="glass rounded-2xl p-6 flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="rounded-lg bg-primary/15 p-2"><Icon className="h-5 w-5 text-primary" /></div>
                    <div className="font-semibold">{t.name}</div>
                  </div>
                  <Badge variant="outline" className={riskColor[t.risk_level] ?? ""}>{t.risk_level} risk</Badge>
                </div>
                <div className="mt-4 font-mono">
                  <span className="text-3xl font-semibold">{Number(t.daily_roi)}%</span>
                  <span className="text-sm text-muted-foreground"> / day</span>
                </div>
                <div className="text-xs text-muted-foreground font-mono">
                  {fmtMoney(t.min_amount)} – {t.max_amount ? fmtMoney(t.max_amount) : "∞"} · {t.duration_days}d
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{t.description}</p>
                <ul className="mt-4 space-y-1.5 text-sm flex-1">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Dialog open={openId === t.id} onOpenChange={(o) => { setOpenId(o ? t.id : null); setAmount(String(t.min_amount)); }}>
                  <DialogTrigger asChild>
                    <Button className="mt-5 w-full">Activate bot</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Activate {t.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="rounded-lg bg-muted/30 p-3 text-xs space-y-1">
                        <div className="flex justify-between"><span className="text-muted-foreground">Available balance</span><span className="font-mono">{fmtMoney(profile?.balance)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Daily ROI</span><span className="font-mono">{Number(t.daily_roi)}%</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Duration</span><span className="font-mono">{t.duration_days} days</span></div>
                      </div>
                      <div className="space-y-2">
                        <Label>Investment amount (USD)</Label>
                        <Input type="number" min={t.min_amount} max={t.max_amount ?? undefined} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Lock className="h-3 w-3" /> Funds are locked until the bot completes its {t.duration_days}-day cycle.
                        </p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="ghost" onClick={() => setOpenId(null)}>Cancel</Button>
                      <Button
                        disabled={activate.isPending}
                        onClick={() => {
                          const amt = Number(amount);
                          if (!amt || amt < Number(t.min_amount)) return toast.error(`Minimum is ${fmtMoney(t.min_amount)}`);
                          if (t.max_amount && amt > Number(t.max_amount)) return toast.error(`Maximum is ${fmtMoney(t.max_amount)}`);
                          activate.mutate({ tier_id: t.id, amount: amt }, { onSuccess: () => setOpenId(null) });
                        }}
                      >
                        {activate.isPending ? "Activating…" : "Confirm & activate"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            );
          })}
        </div>
      </section>

      <div className="glass rounded-2xl p-5 text-xs text-muted-foreground">
        <strong className="text-foreground">How it works:</strong> Activating a bot moves the chosen amount from your balance into the bot. Profits accrue at the bot's daily ROI rate and can be claimed any time. The principal returns to your balance when the bot's cycle completes.
      </div>
    </div>
  );
}